import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("zz_cart") || "[]"); } catch { return []; }
  });
  const [a11y, setA11y] = useState(() => {
    try { return JSON.parse(localStorage.getItem("zz_a11y") || "{}"); } catch { return {}; }
  });

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data && data.user_id ? data : null);
    } catch {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setAuthLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    localStorage.setItem("zz_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem("zz_a11y", JSON.stringify(a11y));
    const b = document.body;
    b.classList.toggle("zz-large-text", a11y.text === "large");
    b.classList.toggle("zz-xlarge-text", a11y.text === "xlarge");
    b.classList.toggle("zz-dyslexic", !!a11y.dyslexic);
    b.classList.toggle("zz-high-contrast", !!a11y.highContrast);
    b.classList.toggle("zz-reduce-motion", !!a11y.reduceMotion);
  }, [a11y]);

  const addToCart = (product, qty = 1) => {
    setCart((c) => {
      const i = c.findIndex((it) => it.product_id === product.product_id);
      if (i >= 0) {
        const cp = [...c]; cp[i] = { ...cp[i], quantity: cp[i].quantity + qty }; return cp;
      }
      return [...c, { product_id: product.product_id, name: product.name, price: product.price, image_url: product.image_url, quantity: qty }];
    });
  };
  const updateQty = (pid, qty) => setCart((c) => c.map((it) => it.product_id === pid ? { ...it, quantity: Math.max(1, qty) } : it));
  const removeFromCart = (pid) => setCart((c) => c.filter((it) => it.product_id !== pid));
  const clearCart = () => setCart([]);
  const cartCount = cart.reduce((s, it) => s + it.quantity, 0);
  const cartTotal = cart.reduce((s, it) => s + it.quantity * it.price, 0);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  return (
    <AppContext.Provider value={{
      user, setUser, authLoading, checkAuth, logout,
      cart, addToCart, updateQty, removeFromCart, clearCart, cartCount, cartTotal,
      a11y, setA11y,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
